import { BoxV2 as Box, Button, Flex, Table, useTable } from 'portal-commons';
import { useState, useEffect, useMemo, useRef } from 'react';
import {
  faEnvelope,
  faAddressBook,
  faCircleCheck,
  faCircleUser,
  faPen,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  getUsers,
  getProfile,
  createUser,
  deleteUser,
  enableMfa,
  disableMfa,
  getApprovedDcaList,
  updateProfile,
} from '../api';
import {
  Text,
  Divider,
  DetailItem,
  UserCreateModal,
  UserDeleteModal,
  MfaToggleModal,
  EmailPreferenceEditModal,
  UserListingRow,
  SectionBlock,
} from '../components';
import { ApprovedDca, CreateUserPayload, Profile, User } from '../types';
import { Loader } from '@/shared_elements';
import { toastFlashMessage } from '@/utils';
import { useAuthContext } from '@/contexts';

const USER_TABLE_COLUMNS = [
  { id: 'fullName', label: 'NAME', sortable: true },
  { id: 'email', label: 'EMAIL', sortable: false },
  { id: 'created', label: 'CREATED ON', sortable: true },
  { id: 'lastLogin', label: 'LAST LOGIN', sortable: true },
  { id: 'action', label: '', sortable: false },
];

enum Scene {
  Details = 'details',
  EditEmailPreferences = 'editEmailPreferences',
  CreateUser = 'createUser',
  DeleteUser = 'deleteUser',
  ChangeMfa = 'changeMfa',
}

type LoadingType =
  | 'profile'
  | 'createUser'
  | 'deleteUser'
  | 'mfaChange'
  | 'editEmails';

const TABLE_PAGE_SIZE = 10;

export const ProfilePage = () => {
  const authContext = useAuthContext();
  // FR-805. Hide it before Germany DCA portal created
  const isDcaListHidden = authContext.profile?.market === 'DE';
  const [profile, setProfile] = useState<Profile>();
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [scene, setScene] = useState<Scene>(Scene.Details);
  const [editingUser, setEditingUser] = useState<User>();
  const [loading, setLoading] = useState<Record<LoadingType, boolean>>({
    profile: false,
    createUser: false,
    deleteUser: false,
    mfaChange: false,
    editEmails: false,
  });

  const approvedDcaTable = useTable<ApprovedDca>(
    async () => {
      const dcaList = !isDcaListHidden
        ? (await getApprovedDcaList()) || []
        : [];

      return {
        data: dcaList,
        total: dcaList.length || 0,
        rowsPerPage: Math.max(dcaList.length || 0, TABLE_PAGE_SIZE),
        page: 1,
      };
    },
    {
      deps: [isDcaListHidden],
      defaultFilter: {
        sortField: 'displayName',
        ascendingOrder: true,
      },
      inMemory: true,
    }
  );

  const userSkipFetchRef = useRef(false);
  const userTable = useTable<User>(
    async (filter) => {
      if (userSkipFetchRef.current) {
        const data: User[] = userTable.tableData.slice() || [];
        return {
          data,
          total: data.length,
          rowsPerPage: TABLE_PAGE_SIZE,
          page: filter?.page || 1,
        };
      }

      const users = (await getUsers()) || [];
      userSkipFetchRef.current = true;
      return {
        data: users,
        total: users.length,
        rowsPerPage: TABLE_PAGE_SIZE,
        page: filter?.page || 1,
      };
    },
    {
      deps: [],
      defaultFilter: {
        sortField: 'created',
        ascendingOrder: false,
      },
      inMemory: true,
    }
  );

  const isMaxUsersReached =
    !!profile?.maxPortalUsers &&
    userTable.tableData.length >= profile?.maxPortalUsers;

  const setLoadingByType = (key: LoadingType, value: boolean) => {
    setLoading((prev) => ({ ...prev, [key]: value }));
  };

  const fetchProfile = async () => {
    setLoadingByType('profile', true);
    const profile = await getProfile();
    setLoadingByType('profile', false);
    if (profile) {
      setProfile(profile);
    }
  };

  const reloadUserTable = () => {
    userSkipFetchRef.current = false;
    userTable.reload();
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    setMfaEnabled(profile?.mfaEnabled ?? false);
  }, [profile]);

  const handleCreateUser = async (payload: CreateUserPayload) => {
    setLoadingByType('createUser', true);
    const success = await createUser(payload);
    setLoadingByType('createUser', false);
    if (success) {
      reloadUserTable();
      setScene(Scene.Details);
      toastFlashMessage(
        `New User ${payload.firstName} ${payload.lastName} Created`,
        'success'
      );
    }
  };

  const handleDeleteUser = async () => {
    if (editingUser) {
      setLoadingByType('deleteUser', true);
      const success = await deleteUser(editingUser.id);
      setLoadingByType('deleteUser', false);
      if (success) {
        reloadUserTable();
        setScene(Scene.Details);
        toastFlashMessage(
          `User ${editingUser.profile.fullName} Deleted`,
          'success'
        );
      }
    }
  };

  const handleMfaChange = async () => {
    setLoadingByType('mfaChange', true);
    const success = mfaEnabled ? await disableMfa() : await enableMfa();
    setLoadingByType('mfaChange', false);
    if (success) {
      setScene(Scene.Details);
      toastFlashMessage(
        `MFA ${mfaEnabled ? 'deactivated' : 'activated'} for all portal users`,
        'success'
      );
      setMfaEnabled(!mfaEnabled);
    }
  };

  const handleEmailPreferenceSave = async (emails: string[]) => {
    setLoadingByType('editEmails', true);
    const profile = await updateProfile({
      email: emails.map((e) => e.trim()).join(','),
    });
    setLoadingByType('editEmails', false);
    if (profile) {
      setScene(Scene.Details);
      toastFlashMessage('Email Address Successfully Updated', 'success');
      setProfile(profile);
    }
  };

  const approvedDcaTableData = useMemo(() => {
    return (
      approvedDcaTable.tableData
        .slice()
        .sort((a, b) => {
          if (approvedDcaTable.filter.sortField === 'displayName') {
            return approvedDcaTable.filter.ascendingOrder
              ? a.displayName.localeCompare(b.displayName)
              : b.displayName.localeCompare(a.displayName);
          }
          return 0;
        })
        // add this map() to avoid TSLint error for <Table>
        .map((dca) => ({
          ...dca,
        }))
    );
  }, [approvedDcaTable.tableData, approvedDcaTable.filter]);

  const usersTableData = useMemo(() => {
    const { sortField, ascendingOrder, page: currentPage } = userTable.filter;
    return userTable.tableData
      .slice()
      .sort((a, b) => {
        if (sortField === 'fullName') {
          return ascendingOrder
            ? a.profile.fullName.localeCompare(b.profile.fullName)
            : b.profile.fullName.localeCompare(a.profile.fullName);
        } else if (sortField === 'created' || sortField === 'lastLogin') {
          return ascendingOrder
            ? a[sortField] - b[sortField]
            : b[sortField] - a[sortField];
        }

        return 0;
      })
      .slice(
        (currentPage - 1) * TABLE_PAGE_SIZE,
        currentPage * TABLE_PAGE_SIZE
      );
  }, [userTable.filter, userTable.tableData]);

  if (loading.profile) {
    return <Loader />;
  }

  const emails = profile?.email?.split(',') ?? [];

  return (
    <div className="page-content" data-testid="mnoProfilePage">
      <Text
        variant="h1"
        sx={{ mb: '57px', display: 'block', textAlign: 'center' }}
      >
        {profile?.displayName}
      </Text>
      <SectionBlock>
        <Flex sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <Flex sx={{ gap: 'xs', mb: 'xl', alignItems: 'center' }}>
            <SectionBlock.Icon size="xl" icon={faEnvelope} />
            <Text variant="h2">Email Preferences</Text>
          </Flex>
          <Flex
            sx={{
              flexDirection: 'row',
              columnGap: '4px',
              color: 't.green140',
              fontWeight: 'normal',
              fontSize: 'H300',
              lineHeight: 'H400',
              cursor: 'pointer',
            }}
            role="button"
            onClick={() => setScene(Scene.EditEmailPreferences)}
            data-testid="editEmailPreferencesButton"
          >
            <FontAwesomeIcon icon={faPen} />
            <span>Edit</span>
          </Flex>
        </Flex>
        <Flex sx={{ flexDirection: 'column', gap: 'xs' }}>
          <Flex sx={{ gap: 'xs', alignItems: 'center' }}>
            <SectionBlock.Icon size="md" icon={faAddressBook} />
            <Text variant="h3">Contact Email</Text>
          </Flex>
          <Text variant="body">
            Used for business communications and system event notifications.
          </Text>
          <Divider />
        </Flex>
        <Box
          data-testid="contactEmailsRoot"
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 's',
            mt: 's',
          }}
        >
          {emails.map((email: string, index: number) => (
            <DetailItem key={`${index}-${email}`} label="Email Address">
              {email}
            </DetailItem>
          ))}
        </Box>
      </SectionBlock>

      {!isDcaListHidden && (
        <SectionBlock>
          <Flex sx={{ gap: 'xs', alignItems: 'center' }}>
            <SectionBlock.Icon size="xl" icon={faCircleCheck} />
            <Text variant="h2">MNO Approved DCAs</Text>
          </Flex>
          <Divider sx={{ marginY: 'm' }} />
          <Table
            data-testid="approvedDcaTable"
            disableHover
            loading={approvedDcaTable.loading}
            emptyState="no DCA to view"
            headRows={[{ id: 'displayName', label: 'NAME', sortable: true }]}
            data={approvedDcaTableData}
            rowKey="uid"
            filter={approvedDcaTable.filter}
            createSortHandler={approvedDcaTable.handleSorting}
          />
        </SectionBlock>
      )}

      <SectionBlock>
        <Flex sx={{ mb: 'l', justifyContent: 'space-between' }}>
          <Flex sx={{ gap: 'xs', alignItems: 'center' }}>
            <SectionBlock.Icon size="xl" icon={faCircleUser} />
            <Text variant="h2">User Management</Text>
          </Flex>
          <Button
            shape="square"
            size="small"
            onClick={() => {
              setScene(Scene.CreateUser);
            }}
            disabled={userTable.loading || isMaxUsersReached}
            data-testid="createUserButton"
          >
            Create New User
          </Button>
        </Flex>
        <Divider sx={{ marginY: 'm' }} />
        <Table
          disableHover
          testId="usersTable"
          rowKey="id"
          loading={userTable.loading}
          emptyState="No users"
          headRows={USER_TABLE_COLUMNS}
          filter={userTable.filter}
          handleChangePage={userTable.handleChangePage}
          createSortHandler={userTable.handleSorting}
          tableData={usersTableData.map((user) => (
            <UserListingRow
              key={user.id}
              user={user}
              onDelete={() => {
                setEditingUser(user);
                setScene(Scene.DeleteUser);
              }}
            />
          ))}
          pagination={userTable.pagination}
        />
        {isMaxUsersReached && (
          <Box sx={{ mt: 'l', textAlign: 'center' }}>
            Maximum number of users reached!
            <br />
            Please delete existing users if you wish to create a new one.
          </Box>
        )}
      </SectionBlock>
      <UserCreateModal
        open={scene === Scene.CreateUser}
        onClose={() => setScene(Scene.Details)}
        onSubmit={handleCreateUser}
        disabled={loading.createUser}
      />
      <UserDeleteModal
        open={scene === Scene.DeleteUser}
        user={editingUser}
        onClose={() => setScene(Scene.Details)}
        onSubmit={handleDeleteUser}
        disabled={loading.deleteUser}
      />
      <MfaToggleModal
        on={!mfaEnabled}
        disabled={loading.mfaChange}
        open={scene === Scene.ChangeMfa}
        onClose={() => setScene(Scene.Details)}
        onSubmit={handleMfaChange}
      />
      <EmailPreferenceEditModal
        open={scene === Scene.EditEmailPreferences}
        emails={emails}
        disabled={loading.editEmails}
        onClose={() => setScene(Scene.Details)}
        onSubmit={handleEmailPreferenceSave}
      />
    </div>
  );
};

export default ProfilePage;
